/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/display-name */
import ArticleSelectorDialog from 'components/Common/ArticleSelector/ArticleSelectorDialog'
import { AssemblySimplificationDialog } from 'components/Common/AssemblySimplification/AssemblySimplificationDialog'
import { BatchEditRows } from 'features/BillOfMaterials/BatchEditRowsDialog/BatchEditRowsDialog'
import { CuttingPlanDialog } from 'features/BillOfMaterials/MaterialHeader/ActiveCuttingPlanDialog'
import { BomItemDetailsDrawer } from 'features/BomItemDetailsDrawer/BomItemDetailsDrawer'
import { ConfirmationDialog } from 'features/ConfirmationDialog/ConfirmationDialog'
import { CostPriceDetailsDialog } from 'features/CostPriceDetails/CostPriceDetailsDialog'
import { EventLogsDialog } from 'features/EventLogs/components/EventLogsDialog'
import { ProjectOperationsDialog } from 'features/GeometryLogs/ProjectOperationsLogsDrawer'
import { PurchasingPartDialog } from 'features/PurchasingPartDetails/PurchasingPartDetails'
import { SalesRepresentativeDialog } from 'features/SalesRepresentative/components/SalesRepresentativeDialog'
import { SupportContactDialog } from 'features/SupportContactDialog/SupportContactDialog'
import React, { useCallback, useRef } from 'react'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import { RootAppState } from 'store/configureStore'
import { UIActions } from 'store/UI/UIActions'
import { ModalTypeProps, ModalTypes } from 'store/UI/UITypes'
import WorkingStepsDialog from '../../components/ProjectPage/AdditionalWorkingSteps/Dialog/WorkingStepsDialog'
import { RowDetailsDialog } from '../../components/ProjectPage/BillOfMaterials/Rows/Components/RowDetailsDialog/RowDetailsDialog'
import { IssuesDialog } from '../../components/ProjectPage/BillOfMaterials/Rows/PartType/IssuesDialog'
import { FinancialDetailsDialog } from '../../components/ProjectPage/BillOfMaterials/Rows/TargetPrice/FinancialDetailsDialog'

const openedModalsStack: Array<{ modalType: ModalTypes; modalProps: any }> = []

export const useDialogManager = () => {
  const dispatch = useAppDispatch()

  const openDialog = useCallback(
    (modalType: ModalTypes, modalProps?: ModalTypeProps) => {
      setTimeout(() => dispatch(UIActions.OpenModal(modalType, modalProps)))
    },
    [dispatch]
  )

  const closeDialog = useCallback(
    (clearStack?: boolean) => {
      dispatch(UIActions.CloseModal())
      if (clearStack) {
        openedModalsStack.length = 0
      }
    },
    [dispatch]
  )

  return {
    openDialog,
    closeDialog,
  }
}

const getDialogComponent = (args: {
  modalType: ModalTypes
  modalProps: any
  onClose: () => void
}) => {
  const modals: Record<ModalTypes, () => React.ReactElement> = {
    ArticleSelector: () => (
      <ArticleSelectorDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    AssemblySimplification: () => (
      <AssemblySimplificationDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    BatchEditBomItems: () => (
      <BatchEditRows {...args.modalProps} open onClose={args.onClose} />
    ),
    CuttingPlanDetails: () => (
      <CuttingPlanDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    BomItemDetailsDrawer: () => (
      <BomItemDetailsDrawer {...args.modalProps} open onClose={args.onClose} />
    ),
    ConfirmationDialog: () => (
      <ConfirmationDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    CostPriceDetails: () => (
      <CostPriceDetailsDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    EventLogs: () => (
      <EventLogsDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    ProjectOperations: () => (
      <ProjectOperationsDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    SalesRepresentative: () => (
      <SalesRepresentativeDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    SupportContact: () => (
      <SupportContactDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    WorkingSteps: () => (
      <WorkingStepsDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    RowDetails: () => (
      <RowDetailsDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    IssuesDialog: () => (
      <IssuesDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    FinancialDetails: () => (
      <FinancialDetailsDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    PurchasingPartDialog: () => (
      <PurchasingPartDialog {...args.modalProps} open onClose={args.onClose} />
    ),
  }

  return modals[args.modalType]
}

export const DialogManager = () => {
  const activeModal = useAppSelector((state: RootAppState) => state.ui.Modals)

  const { closeDialog, openDialog } = useDialogManager()

  const currentOpenedModalType = useRef(undefined)

  const dialogFactory = getDialogComponent({
    modalType: activeModal?.CurrentModal,
    modalProps: activeModal?.ModalProps,
    onClose: closeDialog,
  })

  if (dialogFactory) {
    // push to opened modals stack
    openedModalsStack.push({
      modalType: activeModal.CurrentModal,
      modalProps: activeModal.ModalProps,
    })

    currentOpenedModalType.current = activeModal.CurrentModal

    return dialogFactory()
  } else {
    // pop from opened modals stack (this will be the currently visible modal)
    let lastOpenedModal = openedModalsStack.pop()

    while (
      lastOpenedModal &&
      lastOpenedModal?.modalType === currentOpenedModalType.current
    ) {
      lastOpenedModal = openedModalsStack.pop()
    }

    if (lastOpenedModal) {
      openDialog(lastOpenedModal.modalType, lastOpenedModal.modalProps)
      return null
    } else {
      currentOpenedModalType.current = undefined
      return null
    }
  }
}
